import { useTranslation } from "react-i18next"
import { useQuery } from "@apollo/client"
import { useState, useRef } from "react"
import { GET_ACTIVITIES } from "../../api/QuadriApi"
import { UserRow } from "../UserRow"
import { formatDateAsConnect } from "../../util/dateTools"
import { useRecoilValue, useRecoilValueLoadable } from "recoil"
import DateRangePicker from "../FromConnect/DateRangePicker"
import { getOnFilterChanged } from "../../util/filtering"
import { Activity, ActivitiesFilter, ActivityType } from "../../types"
import { projectOriginState, quadriProjectIdState, usersByTidUuidState } from "../StateSelectors"
import UserPicker from "../FromConnect/UserPicker"
import { ActivityFilter } from "../Timeline/TimelineMain"

interface ActivitiesProps {
  projectId: string
  taskId: number | undefined
}

const TasksActivities = (props: ActivitiesProps) => {
  const projectId = props.projectId
  const projectOrigin = useRecoilValue(projectOriginState)
  const taskId = props.taskId
  const quadriProjectId = useRecoilValue(quadriProjectIdState)
  const users = useRecoilValueLoadable(usersByTidUuidState)
  const [filter, setFilter] = useState({} as ActivityFilter)
  const { t } = useTranslation("tasks")

  const queryFilter = getDefaultFilter(taskId)

  const {
    loading: actLoading,
    error: actError,
    data: actData
  } = useQuery(GET_ACTIVITIES, {
    fetchPolicy: "no-cache",
    variables: { quadriProjectId, filter: queryFilter },
    errorPolicy: "all"
  })

  const onFilterChange = getOnFilterChanged(setFilter)

  function filterCheck(entry: Activity, theFilter: ActivitiesFilter, type: ActivityType) {
    const latestChangeDate = new Date(entry.time)
    const fromDate = new Date(theFilter.startDate ?? "1990-01-01")
    const toDate = new Date(theFilter.endDate ?? "2990-01-01")
    if (fromDate > latestChangeDate || toDate < latestChangeDate) {
      return false
    }
    if (theFilter.tidUserIds?.length) {
      if (!theFilter.tidUserIds?.find((uId: any) => uId === entry.userId)) return false
    }
    return true
  }

  if (actLoading) {
    return <div>{t("tasksActivities.loading")}</div>
  } else if (actError) {
    return (
      <div>
        <p>{t("tasksActivities.errLoading")}</p>
        <p>{actError.message}</p>
      </div>
    )
  }

  const usersApiUrl = `${projectOrigin}/projects/${projectId}/users?includesRemoved=false`

  let Activities = [...actData?.getActivities]

  const filteredActivities = Activities.filter((item) => {
    return filterCheck(item, filter, item.type)
  })

  return (
    <div>
      <div>
        <div className="filters border-bottom">
          <ul>
            <li className="filter-type lift-bottom">
              <UserPicker
                id="activity-user-picker"
                onChange={onFilterChange}
                apiUrl={usersApiUrl}
                selectionList={filter.connectUserIds || []}
              />
            </li>
            <li className="filter-type">
              <DateRangePicker
                onChange={onFilterChange}
                id="tasks-date-range-picker"
                selection={{
                  from: filter.startDate || null,
                  to: filter.endDate || null
                }}
              />
            </li>
          </ul>
        </div>
        <ul>
          {filteredActivities.map((activityData, index) => (
            <li className="selectable pt-1 pl-1 pr-1 border-bottom" key={index.toString()}>
              <div className="col">
                <div>
                  <p className="pb-1 small">{formatDateAsConnect(activityData.time)}</p>
                  <UserRow
                    connectProjectId={projectId}
                    userId={users.contents[activityData.userId]}
                    user={users.contents[activityData.userId]}
                    taskChanges={activityData.taskChanges}
                    taskId={taskId}
                  />
                </div>
                <p className="pt-1 pb-1 small">{activityData.type + ": " + activityData.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

const getDefaultFilter = (taskId: any) => ({
  taskId: taskId,
  types: ["Share"]
})

export default TasksActivities
